/**
 * closures/{id} — studio closed days (spring break, holidays, snow days).
 *
 * A closure is a single day or an inclusive date range the studio is shut.
 * Dates are stored as 'YYYY-MM-DD' strings so they compare the same
 * lexicographically as chronologically (no timezone math, no Timestamp
 * juggling for an all-day concept).
 *
 * Phase "calendar overhaul" PR 1. The next slice (recurring class times)
 * reads these to skip generating class sessions on closed days; the calendar
 * already surfaces them as a banner.
 *
 * Rules: any signed-in user reads (everyone's calendar shows closures);
 * Director writes. See shared/firestore.rules.
 */

import { useEffect, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './auth-context';
import { normalizeClosureInput } from './closureSpan';

export interface ClosureDoc {
  id: string;
  /** Inclusive start, 'YYYY-MM-DD'. */
  startDate: string;
  /** Inclusive end, 'YYYY-MM-DD'. Equals startDate for a single day. */
  endDate: string;
  /** Human label, e.g. "Spring Break". */
  label: string;
  createdAt: Timestamp | FieldValue;
  createdBy: string;
}

export interface UseClosuresResult {
  loading: boolean;
  closures: ClosureDoc[];
  error: string | null;
  /**
   * True until Firestore confirms this listener from the server. Starts true
   * (never a false "confirmed" claim before the first snapshot lands) and
   * only a real server snapshot clears it - same posture and same reason as
   * useAllClassSeries' fromCache in lib/classSeries.ts: `loading` alone goes
   * false the instant a CACHED first snapshot arrives, which reads as
   * "confirmed" when it may really be stale offline data. A caller doing its
   * own honest-state gating (Teach.tsx's own trackFromCache comment; see
   * components/SocialMediaReminderCard.tsx) needs this to tell "empty
   * because it is empty" from "empty because the cache had nothing yet."
   */
  fromCache: boolean;
}

/** Subscribe to every closure, earliest start first. */
export function useClosures(): UseClosuresResult {
  const [closures, setClosures] = useState<ClosureDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(true);

  useEffect(() => {
    if (!db) {
      setError('Firestore not initialized.');
      setLoading(false);
      return;
    }
    const q = query(collection(db, 'closures'), orderBy('startDate'));
    const unsub = onSnapshot(
      q,
      // includeMetadataChanges so a cache-then-server pair with IDENTICAL
      // data still re-fires this callback purely to flip fromCache false -
      // without this a same-data server confirmation is a metadata-only
      // change Firestore would otherwise never deliver here, and fromCache
      // would read true forever. Same option classSeries.ts and
      // classCancellations.ts already use for the same reason.
      { includeMetadataChanges: true },
      (snap) => {
        setClosures(
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<ClosureDoc, 'id'>),
          }))
        );
        setError(null);
        setFromCache(snap.metadata.fromCache);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  return { loading, closures, error, fromCache };
}

export interface NewClosureInput {
  startDate: string;
  endDate: string;
  label: string;
}

/** Hook returning a create function bound to the current Director. */
export function useCreateClosure() {
  const { user } = useAuth();
  return async (input: NewClosureInput): Promise<string> => {
    if (!db) throw new Error('Firestore not initialized.');
    if (!user) throw new Error('Sign in required.');
    const clean = normalizeClosureInput(input);
    const ref = await addDoc(collection(db, 'closures'), {
      ...clean,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
    });
    return ref.id;
  };
}

/**
 * Change a closure's dates or its name in place.
 *
 * Bobbie, 2026-08-27: "it also feels like a directors could be able to click
 * on the event on the calendar and be able to edit". Until now a closure was
 * the one thing on the calendar with no edit path anywhere in the Hub, so
 * shortening a break meant deleting it and retyping the whole thing.
 *
 * Only the three fields a Director owns are sent. createdAt and createdBy are
 * left untouched, which is what the Firestore rule requires (closures allow
 * update: if isDirector() && hasClosureShape(...) && auditKept()), and the
 * shape check there rejects any extra field, so there is no updatedAt to add.
 */
export async function updateClosure(
  id: string,
  input: NewClosureInput
): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  const clean = normalizeClosureInput(input);
  await updateDoc(doc(db, 'closures', id), { ...clean });
}

export async function deleteClosure(id: string): Promise<void> {
  if (!db) throw new Error('Firestore not initialized.');
  await deleteDoc(doc(db, 'closures', id));
}

/**
 * The closure covering a given 'YYYY-MM-DD' date, or null. String compare is
 * safe because ISO dates sort chronologically.
 */
export function closureForDate(
  closures: ClosureDoc[],
  dateStr: string
): ClosureDoc | null {
  return (
    closures.find((c) => c.startDate <= dateStr && dateStr <= c.endDate) ?? null
  );
}

/**
 * Span math lives in lib/closureSpan.ts, which imports nothing, so the rules
 * for how long a closure runs and how removing it reads stay testable without
 * booting React or Firebase. Re-exported here so every existing caller keeps
 * importing them from the module that owns the collection.
 */
export {
  closureDayCount,
  closureRemovalPrompt,
  formatClosureRange,
  normalizeClosureInput,
  type ClosureInput,
  type ClosureSpan,
} from './closureSpan';
